// Sidebar.js
import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Box,
  Typography,
  Tooltip,
  useMediaQuery,
  useTheme,
  Collapse,
  Avatar,
  IconButton,
  Badge,
  Menu,
  MenuItem,
} from "@mui/material";
import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Business as BusinessIcon,
  ContactMail as ContactMailIcon,
  Engineering as EngineeringIcon,
  Description as DescriptionIcon,
  Article as ArticleIcon,
  Assignment as AssignmentIcon,
  Receipt as ReceiptIcon,
  Settings as SettingsIcon,
  Help as HelpIcon,
  ExpandLess,
  ExpandMore,
  Home as HomeIcon,
  Notifications as NotificationsIcon,
  Mail as MailIcon,
  DarkMode as DarkModeIcon,
  LightMode as LightModeIcon,
  CalendarMonth as CalendarMonthIcon,
  Chat as ChatIcon,
  Logout as LogoutIcon,
  ArrowDropDown as ArrowDropDownIcon,
  LocalShipping as LocalShippingIcon,
  Handshake as HandshakeIcon,
  AccountBalance as AccountBalanceIcon,
  ShoppingCart as ShoppingCartIcon,
  Sell as SellIcon,
  SwapHoriz as SwapHorizIcon,
  ElectricBolt as ElectricBoltIcon,
} from "@mui/icons-material";
import { useThemeContext } from "../../App";
import { useChat } from "../../contexts/ChatContext";
import { useAuth } from "../../hooks/useAuth";
import { useCollection } from "../../hooks/useCollection";
import { usePermissions } from "../../hooks/usePermissions";

const DRAWER_WIDTH = 260;
const DRAWER_COLLAPSED_WIDTH = 64;

const Sidebar = ({ isOpen, toggle }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const location = useLocation();
  const navigate = useNavigate();
  const { darkMode, toggleTheme } = useThemeContext();
  const { toggleChat, totalUnread, openChat } = useChat();
  const { logout, user, userProfile } = useAuth();
  const { data: roles } = useCollection("roles");
  const { hasPermission } = usePermissions();

  // Estados de los submenús
  const [contactsOpen, setContactsOpen] = useState(false);
  const [projectsOpen, setProjectsOpen] = useState(false);
  const [financesOpen, setFinancesOpen] = useState(false);
  const [agendaOpen, setAgendaOpen] = useState(false);
  const [switchOpen, setSwitchOpen] = useState(false);

  // Estados para notificaciones
  const [notificationsAnchor, setNotificationsAnchor] = useState(null);
  const notificationsOpen = Boolean(notificationsAnchor);

  // Estado para el menú de usuario
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);
  const userMenuOpen = Boolean(userMenuAnchor);

  const currentUser = userProfile || user;

  const isCommercialRole = React.useMemo(() => {
    if (!currentUser?.role || !roles) return false;
    const currentRole = roles.find(
      (r) => r.id === currentUser.role || r.name === currentUser.role,
    );
    if (!currentRole) return false;
    const roleName = currentRole.name?.toLowerCase() || "";
    return roleName.includes("comercial") || roleName.includes("commercial");
  }, [currentUser, roles]);

  const handleNotificationsOpen = (event) =>
    setNotificationsAnchor(event.currentTarget);
  const handleNotificationsClose = () => setNotificationsAnchor(null);

  const handleUserMenuOpen = (event) => setUserMenuAnchor(event.currentTarget);
  const handleUserMenuClose = () => setUserMenuAnchor(null);

  const handleLogout = async () => {
    handleUserMenuClose();
    try {
      await logout();
      navigate("/login", { replace: true });
    } catch (error) {
      console.error("Error al cerrar sesión:", error);
    }
  };

  const getUserRoleLabel = (roleId) => {
    if (!roleId) return "Usuario";
    const role = roles?.find((r) => r.id === roleId);
    return role?.name || roleId;
  };

  // Auto-abrir submenús basado en la ruta actual
  useEffect(() => {
    if (
      location.pathname.startsWith("/clients") ||
      location.pathname.startsWith("/contacts") ||
      location.pathname.startsWith("/companies") ||
      location.pathname.startsWith("/suppliers") ||
      location.pathname.startsWith("/commercials")
    ) {
      setContactsOpen(true);
    }
    if (
      location.pathname.startsWith("/proposals") ||
      location.pathname.startsWith("/contracts") ||
      location.pathname.startsWith("/projects")
    ) {
      setProjectsOpen(true);
    }
    if (
      location.pathname.startsWith("/sales") ||
      location.pathname.startsWith("/purchases") ||
      location.pathname.startsWith("/finances") ||
      location.pathname.startsWith("/invoices")
    ) {
      setFinancesOpen(true);
    }
    if (
      location.pathname.startsWith("/calendar") ||
      location.pathname.startsWith("/tasks")
    ) {
      setAgendaOpen(true);
    }
    if (
      location.pathname.startsWith("/switch-clients") ||
      location.pathname.startsWith("/switch-suppliers") ||
      location.pathname.startsWith("/switches") ||
      location.pathname.startsWith("/switch-calendar")
    ) {
      setSwitchOpen(true);
    }
  }, [location.pathname]);

  const isActive = (path) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  const isModuleAllowedForCommercial = (moduleId) => {
    if (!isCommercialRole) return true;
    const commercialAllowedModules = ["proposals"];
    return commercialAllowedModules.includes(moduleId);
  };

  const contactSubItems = [
    ...(hasPermission("contacts") && isModuleAllowedForCommercial("contacts")
      ? [{ label: "Contactos", icon: <ContactMailIcon />, path: "/contacts" }]
      : []),
    ...(hasPermission("clients") && isModuleAllowedForCommercial("clients")
      ? [{ label: "Clientes", icon: <PeopleIcon />, path: "/clients" }]
      : []),
    ...(hasPermission("suppliers") && isModuleAllowedForCommercial("suppliers")
      ? [
          {
            label: "Proveedores",
            icon: <LocalShippingIcon />,
            path: "/suppliers",
          },
        ]
      : []),
    ...(hasPermission("commercials") &&
    isModuleAllowedForCommercial("commercials")
      ? [
          {
            label: "Comerciales",
            icon: <HandshakeIcon />,
            path: "/commercials",
          },
        ]
      : []),
    ...(hasPermission("companies") && isModuleAllowedForCommercial("companies")
      ? [{ label: "Empresas", icon: <BusinessIcon />, path: "/companies" }]
      : []),
  ];

  const projectSubItems = [
    ...(hasPermission("proposals") && isModuleAllowedForCommercial("proposals")
      ? [{ label: "Propuestas", icon: <DescriptionIcon />, path: "/proposals" }]
      : []),
    ...(hasPermission("contracts") && isModuleAllowedForCommercial("contracts")
      ? [{ label: "Contratos", icon: <ArticleIcon />, path: "/contracts" }]
      : []),
    ...(hasPermission("projects") && isModuleAllowedForCommercial("projects")
      ? [{ label: "Proyectos", icon: <AssignmentIcon />, path: "/projects" }]
      : []),
  ];

  const financeSubItems = [
    ...(hasPermission("sales") && isModuleAllowedForCommercial("sales")
      ? [
          { label: "Facturación", icon: <ReceiptIcon />, path: "/invoices" },
          { label: "Ventas (Facturas)", icon: <SellIcon />, path: "/sales" },
        ]
      : []),
    ...(hasPermission("purchases") && isModuleAllowedForCommercial("purchases")
      ? [
          {
            label: "Compras (Facturas)",
            icon: <ShoppingCartIcon />,
            path: "/purchases",
          },
        ]
      : []),
  ];

  const agendaSubItems = [
    ...(hasPermission("calendar") && isModuleAllowedForCommercial("calendar")
      ? [
          {
            label: "Calendario",
            icon: <CalendarMonthIcon />,
            path: "/calendar",
          },
        ]
      : []),
    ...(hasPermission("tasks") && isModuleAllowedForCommercial("tasks")
      ? [{ label: "Tareas", icon: <AssignmentIcon />, path: "/tasks" }]
      : []),
  ];

  // NUEVO: Submenú de Comercializadora
  const switchSubItems = [
    { label: "Clientes", icon: <PeopleIcon />, path: "/switch-clients" },
    {
      label: "Comercializadoras",
      icon: <BusinessIcon />,
      path: "/switch-suppliers",
    },
    { label: "Trámites", icon: <SwapHorizIcon />, path: "/switches" },
    {
      label: "Calendario",
      icon: <CalendarMonthIcon />,
      path: "/switch-calendar",
    },
  ];

  const getHomeRoute = () => {
    if (isCommercialRole) return "/proposals";
    return "/";
  };

  const mainMenuItems = [
    ...(!isCommercialRole &&
    contactSubItems.length === 0 &&
    projectSubItems.length === 0 &&
    financeSubItems.length === 0 &&
    agendaSubItems.length === 0
      ? [
          {
            label: "Home",
            icon: <HomeIcon />,
            path: "/dashboard",
            hasSubmenu: false,
          },
        ]
      : []),
    ...(contactSubItems.length > 0
      ? [
          {
            label: "Contactos",
            icon: <PeopleIcon />,
            hasSubmenu: true,
            submenuOpen: contactsOpen,
            toggleSubmenu: () => setContactsOpen(!contactsOpen),
            submenuPath: "/contacts",
            items: contactSubItems,
          },
        ]
      : []),
    ...(projectSubItems.length > 0
      ? [
          {
            label: "Proyectos",
            icon: <EngineeringIcon />,
            hasSubmenu: true,
            submenuOpen: projectsOpen,
            toggleSubmenu: () => setProjectsOpen(!projectsOpen),
            submenuPath: "/projects",
            items: projectSubItems,
          },
        ]
      : []),
    ...(financeSubItems.length > 0
      ? [
          {
            label: "Finanzas",
            icon: <AccountBalanceIcon />,
            hasSubmenu: true,
            submenuOpen: financesOpen,
            toggleSubmenu: () => setFinancesOpen(!financesOpen),
            submenuPath: "/finances",
            items: financeSubItems,
          },
        ]
      : []),
    ...(agendaSubItems.length > 0
      ? [
          {
            label: "Agenda",
            icon: <CalendarMonthIcon />,
            hasSubmenu: true,
            submenuOpen: agendaOpen,
            toggleSubmenu: () => setAgendaOpen(!agendaOpen),
            submenuPath: "/calendar",
            items: agendaSubItems,
          },
        ]
      : []),
    // NUEVO: Grupo Comercializadora
    ...(switchSubItems.length > 0 && !isCommercialRole // Añadir esta condición
      ? [
          {
            label: "Comercializadora",
            icon: <ElectricBoltIcon />,
            hasSubmenu: true,
            submenuOpen: switchOpen,
            toggleSubmenu: () => setSwitchOpen(!switchOpen),
            submenuPath: "/switches",
            items: switchSubItems,
          },
        ]
      : []),
  ];

  const bottomItems = [
    ...(!isCommercialRole && hasPermission("settings")
      ? [{ text: "Configuración", icon: <SettingsIcon />, path: "/settings" }]
      : []),
    ...(!isCommercialRole && hasPermission("chat")
      ? [
          {
            text: "Chat",
            icon: <ChatIcon />,
            onClick: toggleChat,
            badge: totalUnread,
          },
        ]
      : []),
  ];

  const sidebarContent = (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        backgroundColor: darkMode ? theme.palette.background.paper : "#005f65",
        color: "white",
        transition: "background-color 0.3s ease",
        overflowX: "hidden",
        WebkitOverflowScrolling: "touch",
      }}
    >
      <Box
        sx={{
          p: { xs: 1.5, sm: 2 },
          display: "flex",
          alignItems: "center",
          gap: { xs: 1, sm: 1.5 },
          minHeight: { xs: 56, sm: 64 },
          borderBottom: "1px solid rgba(255,255,255,0.15)",
          cursor: "pointer",
        }}
        onClick={() => navigate(getHomeRoute())}
      >
        <Box
          sx={{
            width: { xs: 32, sm: 36 },
            height: { xs: 32, sm: 36 },
            borderRadius: 2,
            backgroundColor: "#000000",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            overflow: "hidden",
            boxShadow: "0 2px 8px rgba(0,0,0,0.4)",
          }}
        >
          <Box
            component="img"
            src={`${process.env.PUBLIC_URL}/epcorelogo.png`}
            alt="EPCore Logo"
            sx={{ width: "90%", height: "90%", objectFit: "contain" }}
          />
        </Box>
        <Box
          sx={{
            width: isOpen ? "auto" : 0,
            overflow: "hidden",
            whiteSpace: "nowrap",
            transition: "width 0.3s ease",
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              fontSize: { xs: "0.95rem", sm: "1.1rem" },
              lineHeight: 1.2,
              opacity: isOpen ? 1 : 0,
              transition: "opacity 0.2s ease",
            }}
          >
            EPCore
          </Typography>
        </Box>
      </Box>

      {/* Navegación principal */}
      <List
        sx={{
          flex: 1,
          pt: { xs: 1, sm: 1.5 },
          px: { xs: 0.5, sm: 1 },
          overflow: "auto",
          WebkitOverflowScrolling: "touch",
        }}
      >
        {mainMenuItems.map((item) => (
          <React.Fragment key={item.label}>
            <ListItem disablePadding sx={{ mb: 0.5 }}>
              <Tooltip title={!isOpen ? item.label : ""} placement="right">
                <ListItemButton
                  onClick={() => {
                    if (item.hasSubmenu) {
                      item.toggleSubmenu();
                    } else {
                      navigate(item.path);
                      if (isMobile) toggle();
                    }
                  }}
                  sx={{
                    borderRadius: 2,
                    minHeight: { xs: 44, sm: 48 },
                    justifyContent: isOpen ? "initial" : "center",
                    px: isOpen ? { xs: 1.5, sm: 2 } : { xs: 1, sm: 1.5 },
                    backgroundColor:
                      isActive(item.path) ||
                      (item.hasSubmenu &&
                        item.submenuPath &&
                        isActive(item.submenuPath))
                        ? "rgba(255,255,255,0.2)"
                        : "transparent",
                    color: "rgba(255,255,255,0.85)",
                    "&:hover": {
                      backgroundColor: "rgba(255,255,255,0.15)",
                      color: "white",
                    },
                    transition: "all 0.2s ease",
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: isOpen ? { xs: 1.5, sm: 2 } : "auto",
                      justifyContent: "center",
                      color: "inherit",
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  {isOpen && (
                    <>
                      <ListItemText
                        primary={item.label}
                        primaryTypographyProps={{
                          fontSize: { xs: "0.8rem", sm: "0.9rem" },
                          fontWeight: 500,
                        }}
                      />
                      {item.hasSubmenu &&
                        (item.submenuOpen ? <ExpandLess /> : <ExpandMore />)}
                    </>
                  )}
                </ListItemButton>
              </Tooltip>
            </ListItem>

            {item.hasSubmenu && isOpen && (
              <Collapse in={item.submenuOpen} timeout="auto" unmountOnExit>
                <List component="div" disablePadding dense>
                  {item.items.map((subItem) => (
                    <ListItem key={subItem.label} disablePadding>
                      <ListItemButton
                        onClick={() => {
                          navigate(subItem.path);
                          if (isMobile) toggle();
                        }}
                        sx={{
                          pl: { xs: 5, sm: 6 },
                          borderRadius: 2,
                          minHeight: { xs: 36, sm: 40 },
                          backgroundColor: isActive(subItem.path)
                            ? "rgba(255,255,255,0.15)"
                            : "transparent",
                          "&:hover": {
                            backgroundColor: "rgba(255,255,255,0.1)",
                          },
                          transition: "all 0.2s ease",
                        }}
                      >
                        <ListItemIcon
                          sx={{ minWidth: 32, color: "rgba(255,255,255,0.8)" }}
                        >
                          {subItem.icon}
                        </ListItemIcon>
                        <ListItemText
                          primary={subItem.label}
                          primaryTypographyProps={{
                            fontSize: { xs: "0.75rem", sm: "0.85rem" },
                            fontWeight: isActive(subItem.path) ? 600 : 400,
                            color: "rgba(255,255,255,0.95)",
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </Collapse>
            )}
          </React.Fragment>
        ))}
      </List>

      <Divider sx={{ borderColor: "rgba(255,255,255,0.15)" }} />

      {/* BOTÓN TEMA OSCURO/CLARO */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-start",
          alignItems: "center",
          px: { xs: 1, sm: 1.5 },
          py: { xs: 0.5, sm: 1 },
          minHeight: { xs: 44, sm: 48 },
        }}
      >
        <Tooltip
          title={darkMode ? "Cambiar a modo claro" : "Cambiar a modo oscuro"}
          placement="right"
        >
          <IconButton
            size="small"
            onClick={toggleTheme}
            sx={{
              width: { xs: 32, sm: 36 },
              height: { xs: 32, sm: 36 },
              color: "rgba(255,255,255,0.8)",
              "&:hover": {
                color: "white",
                backgroundColor: "rgba(255,255,255,0.1)",
              },
              transition: "all 0.2s ease",
            }}
          >
            {darkMode ? (
              <LightModeIcon fontSize="small" />
            ) : (
              <DarkModeIcon fontSize="small" />
            )}
          </IconButton>
        </Tooltip>
      </Box>

      <Divider sx={{ borderColor: "rgba(255,255,255,0.15)" }} />

      {/* Items inferiores */}
      {bottomItems.length > 0 && (
        <List sx={{ p: { xs: 0.5, sm: 1 } }}>
          {bottomItems.map((item) => (
            <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
              <Tooltip title={!isOpen ? item.text : ""} placement="right">
                <ListItemButton
                  onClick={() => {
                    if (item.onClick) {
                      item.onClick();
                    } else {
                      navigate(item.path);
                    }
                    if (isMobile) toggle();
                  }}
                  sx={{
                    borderRadius: 2,
                    minHeight: { xs: 44, sm: 48 },
                    justifyContent: isOpen ? "initial" : "center",
                    px: isOpen ? { xs: 1.5, sm: 2 } : { xs: 1, sm: 1.5 },
                    color: "rgba(255,255,255,0.7)",
                    backgroundColor: isActive(item.path)
                      ? "rgba(255,255,255,0.2)"
                      : "transparent",
                    "&:hover": {
                      backgroundColor: "rgba(255,255,255,0.15)",
                      color: "white",
                    },
                    transition: "all 0.2s ease",
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: isOpen ? { xs: 1.5, sm: 2 } : "auto",
                      justifyContent: "center",
                      color: "inherit",
                      position: "relative",
                    }}
                  >
                    <Badge badgeContent={item.badge || 0} color="error">
                      {item.icon}
                    </Badge>
                  </ListItemIcon>
                  {isOpen && (
                    <ListItemText
                      primary={item.text}
                      primaryTypographyProps={{
                        fontSize: { xs: "0.8rem", sm: "0.9rem" },
                      }}
                    />
                  )}
                </ListItemButton>
              </Tooltip>
            </ListItem>
          ))}
        </List>
      )}

      {/* USUARIO CON MENÚ */}
      {currentUser && (
        <Box
          sx={{
            borderTop: "1px solid rgba(255,255,255,0.15)",
            backgroundColor: "rgba(0,0,0,0.2)",
            transition: "background-color 0.3s ease",
          }}
        >
          {isOpen ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                p: { xs: 1.5, sm: 2 },
                cursor: "pointer",
                "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" },
              }}
              onClick={handleUserMenuOpen}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: { xs: 1, sm: 1.5 },
                }}
              >
                <Avatar
                  sx={{
                    width: { xs: 28, sm: 32 },
                    height: { xs: 28, sm: 32 },
                    background:
                      "linear-gradient(135deg, #00afa3 0%, #00e1d2 100%)",
                    fontSize: { xs: "0.7rem", sm: "0.8rem" },
                    fontWeight: 600,
                    boxShadow: "0 2px 8px rgba(0, 175, 163, 0.3)",
                  }}
                >
                  {(currentUser?.displayName || currentUser?.email || "U")
                    ?.charAt(0)
                    ?.toUpperCase()}
                </Avatar>
                <Box sx={{ overflow: "hidden", flex: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 500,
                      fontSize: { xs: "0.7rem", sm: "0.8rem" },
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {currentUser?.displayName ||
                      currentUser?.email ||
                      "Usuario"}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      opacity: 0.7,
                      fontSize: { xs: "0.6rem", sm: "0.7rem" },
                    }}
                  >
                    {getUserRoleLabel(currentUser?.role)}
                  </Typography>
                </Box>
              </Box>
              <ArrowDropDownIcon
                sx={{ opacity: 0.7, fontSize: { xs: "1.2rem", sm: "1.5rem" } }}
              />
            </Box>
          ) : (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                py: { xs: 1, sm: 1.5 },
                cursor: "pointer",
                "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" },
              }}
              onClick={handleUserMenuOpen}
            >
              <Tooltip
                title={`${currentUser?.displayName || currentUser?.email || "Usuario"} - ${getUserRoleLabel(currentUser?.role)}`}
                placement="right"
              >
                <Avatar
                  sx={{
                    width: { xs: 32, sm: 36 },
                    height: { xs: 32, sm: 36 },
                    background:
                      "linear-gradient(135deg, #00afa3 0%, #00e1d2 100%)",
                    fontSize: { xs: "0.8rem", sm: "0.9rem" },
                    fontWeight: 600,
                    boxShadow: "0 2px 8px rgba(0, 175, 163, 0.3)",
                  }}
                >
                  {(currentUser?.displayName || currentUser?.email || "U")
                    ?.charAt(0)
                    ?.toUpperCase()}
                </Avatar>
              </Tooltip>
            </Box>
          )}
        </Box>
      )}

      {/* Menú de usuario */}
      <Menu
        anchorEl={userMenuAnchor}
        open={userMenuOpen}
        onClose={handleUserMenuClose}
        transformOrigin={{ horizontal: "left", vertical: "bottom" }}
        anchorOrigin={{ horizontal: "left", vertical: "top" }}
        PaperProps={{
          sx: {
            minWidth: { xs: 180, sm: 200 },
            borderRadius: 3,
            mt: -1,
            ml: isOpen ? 0 : { xs: 6, sm: 8 },
            backgroundColor: darkMode ? "#1e1e1e" : "white",
            boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
          },
        }}
      >
        <Box
          sx={{
            px: { xs: 1.5, sm: 2 },
            py: { xs: 1, sm: 1.5 },
            borderBottom: "1px solid rgba(0,0,0,0.1)",
          }}
        >
          <Typography
            variant="subtitle2"
            fontWeight={600}
            fontSize={{ xs: "0.8rem", sm: "0.875rem" }}
          >
            {currentUser?.displayName || "Usuario"}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            fontSize={{ xs: "0.65rem", sm: "0.75rem" }}
          >
            {currentUser?.email || "Sin email"}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: "block",
              fontSize: { xs: "0.65rem", sm: "0.75rem" },
            }}
          >
            {getUserRoleLabel(currentUser?.role)}
          </Typography>
        </Box>

        {!isCommercialRole && hasPermission("settings") && (
          <MenuItem
            onClick={() => {
              handleUserMenuClose();
              navigate("/settings");
            }}
            sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" } }}
          >
            <ListItemIcon>
              <SettingsIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Configuración</ListItemText>
          </MenuItem>
        )}

        <Divider />

        <MenuItem
          onClick={handleLogout}
          sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" } }}
        >
          <ListItemIcon>
            <LogoutIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>
            <Typography
              color="error"
              fontWeight={500}
              fontSize={{ xs: "0.8rem", sm: "0.875rem" }}
            >
              Cerrar Sesión
            </Typography>
          </ListItemText>
        </MenuItem>
      </Menu>

      {/* Menú de notificaciones */}
      <Menu
        anchorEl={notificationsAnchor}
        open={notificationsOpen}
        onClose={handleNotificationsClose}
        transformOrigin={{ horizontal: "left", vertical: "center" }}
        anchorOrigin={{ horizontal: "right", vertical: "center" }}
        PaperProps={{
          sx: {
            minWidth: { xs: 280, sm: 320 },
            maxHeight: { xs: 300, sm: 400 },
            borderRadius: 3,
            mt: 1,
          },
        }}
      >
        <Box sx={{ px: { xs: 1.5, sm: 2 }, py: { xs: 1, sm: 1.5 } }}>
          <Typography
            variant="subtitle1"
            fontWeight={600}
            fontSize={{ xs: "0.9rem", sm: "1rem" }}
          >
            Notificaciones
          </Typography>
        </Box>
        <Divider />
        {[
          { text: "Factura #INV-2024-001 pagada", time: "Hace 5 min" },
          { text: "Nuevo contrato para revisar", time: "Hace 1 hora" },
          { text: "Propuesta pendiente de envío", time: "Hace 3 horas" },
        ].map((notif, index) => (
          <MenuItem
            key={index}
            sx={{ py: { xs: 1, sm: 1.5 } }}
            onClick={handleNotificationsClose}
          >
            <Box sx={{ width: "100%" }}>
              <Typography
                variant="body2"
                fontSize={{ xs: "0.8rem", sm: "0.875rem" }}
              >
                {notif.text}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                fontSize={{ xs: "0.65rem", sm: "0.75rem" }}
              >
                {notif.time}
              </Typography>
            </Box>
          </MenuItem>
        ))}
        <Divider />
        <Box sx={{ textAlign: "center", py: { xs: 0.5, sm: 1 } }}>
          <Typography
            variant="body2"
            color="primary"
            sx={{
              cursor: "pointer",
              fontWeight: 500,
              fontSize: { xs: "0.8rem", sm: "0.875rem" },
            }}
          >
            Ver todas las notificaciones
          </Typography>
        </Box>
      </Menu>
    </Box>
  );

  if (isMobile) {
    return (
      <Drawer
        anchor="left"
        open={isOpen}
        onClose={toggle}
        sx={{
          "& .MuiDrawer-paper": {
            width: DRAWER_WIDTH,
            maxWidth: "85vw",
            borderRight: "none",
          },
        }}
      >
        {sidebarContent}
      </Drawer>
    );
  }

  return (
    <Box
      component="nav"
      sx={{
        width: isOpen ? DRAWER_WIDTH : DRAWER_COLLAPSED_WIDTH,
        flexShrink: 0,
        transition: theme.transitions.create("width", {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.enteringScreen,
        }),
      }}
    >
      <Drawer
        variant="permanent"
        sx={{
          width: isOpen ? DRAWER_WIDTH : DRAWER_COLLAPSED_WIDTH,
          "& .MuiDrawer-paper": {
            width: isOpen ? DRAWER_WIDTH : DRAWER_COLLAPSED_WIDTH,
            boxSizing: "border-box",
            overflowX: "hidden",
            borderRight: "none",
            transition: theme.transitions.create("width", {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
          },
        }}
      >
        {sidebarContent}
      </Drawer>
    </Box>
  );
};

export default Sidebar;
